import { type HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";

type EventHandlerRegistration = {
    eventName: string;
    id?: string;
    handler: (id?: string, data?: unknown) => void;
};

const RECONNECT_POLICY = {
    nextRetryDelayInMilliseconds: ({ previousRetryCount }) => previousRetryCount < 30 ? 1000 : 5000
};

export class WebSocketService {
    readonly #connection: HubConnection;
    #canvasEventHandlers: EventHandlerRegistration[] = [];

    constructor(url: string, accessTokenFactory: () => (string |  Promise<string>)) {
        this.#connection = new HubConnectionBuilder()
            .withUrl(url, { accessTokenFactory })
            .withAutomaticReconnect(RECONNECT_POLICY)
            .build();

        // Automatically rejoin all relevant groups on reconnect...
        this.#connection.onreconnected(() => {
            this.#canvasEventHandlers.forEach(({ eventName, id }) => this.#connection.invoke("SubscribeToEvent", eventName, id));
        });
    }

    /**
     * Starts the WebSocket connection.
     */
    public connect() {
        return this.#connection.start();
    }

    /**
     * Stops the WebSocket connection.
     */
    public disconnect() {
        return this.#connection.stop();
    }

    /**
     * Registers a handler for the given event.
     * 
     * This consists of 2 distinct steps:
     * 1. Subscribing to the provided event on the server side (by adding the hub connection to the relevant event group)
     * 2. Attaching the provided event handler to the client connection
     * 
     * @param eventName - The name of the event to register the handler for.
     * @param id - If provided, the event handler will only listen for events related to the given item id.
     * @param handler - The callback function to invoke when the event occurs.
     * @param isGlobalEvent - If true, the event handler will not be removed when the user navigates away from the current storyline page.
     * @returns A function which can be used to deregister the event handler.
     */
    public async registerEventHandler(eventName: string, id: string | null, handler: (id?: string, data?: unknown) => void, isGlobalEvent: boolean = false): Promise<() => void> {
        const fullEventName = id ? `${eventName}::${id}` : eventName;
        
        // Guard clauses...
        if (eventName?.length < 1)
            throw new Error("Cannot register event handler - event name must be provided.");
        if (!handler)
            throw new Error("Cannot register event handler - no handler provided.");
        if (this.#connection.state !== HubConnectionState.Connected)
            throw new Error("Cannot register event handler - connection is not open.");

        // Register event handler...
        this.#connection.on(fullEventName, handler);

        // Subscribe to event group...
        await this.#connection.invoke("SubscribeToEvent", eventName, id);

        // Keep track of all canvas-level event registrations, so that we can remove them automatically when the user navigates away from the canvas...
        if (!isGlobalEvent) {
            this.#canvasEventHandlers.push({ eventName, id, handler });
        }

        return () => {
            this.deregisterEventHandler(eventName, id, handler);
        };
    }

    /**
     * Deregisters an event handler for the given event.
     * 
     * This consists of 2 distinct steps:
     * 1. Unsubscribing from the provided event on the server side (by removing the hub connection from the relevant event group)
     * 2. Detaching the provided event handler from the client connection
     * 
     * @param eventName - The name of the event to deregister the handler for.
     * @param id - The id of the item to deregister the handler for.  This should match the id provided when registering the handler.
     * @param handler - The specific event handler to deregister.  If not provided, all event handlers for the given event will be deregistered.
     */
    public async deregisterEventHandler(eventName: string, id: string | null, handler?: (id?: string, data?: unknown) => void) {
        const fullEventName = id ? `${eventName}::${id}` : eventName;

        // Unsubscribe from event group...
        if (this.#connection.state === HubConnectionState.Connected) {
            await this.#connection.invoke("UnsubscribeFromEvent", eventName, id);
        }

        // Unregister event handler(s) and remove these from the list of canvas-level event registrations...
        if (handler){
            this.#connection.off(fullEventName, handler);
            this.#canvasEventHandlers = this.#canvasEventHandlers.filter(x => x.eventName !== eventName || x.id !== id || x.handler !== handler);
        }
        else {
            this.#connection.off(fullEventName);
            this.#canvasEventHandlers = this.#canvasEventHandlers.filter(x => x.eventName !== eventName || x.id !== id);
        }
    }

    /**
     * Deregisters all event handlers attached to the current page.
     */
    public async cleanupCanvasEventHandlers() {
        return Promise.all(this.#canvasEventHandlers.map(x => this.deregisterEventHandler(x.eventName, x.id, x.handler)));
    }
}